import {
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Heading,
  IconButton,
  Input,
  SimpleGrid,
  Stack,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation } from "@tanstack/react-query"
import { Fragment, useState } from "react"
import { FiHelpCircle } from "react-icons/fi"

import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tooltip } from "@/components/ui/tooltip"
import { Link } from "@tanstack/react-router"

import { DashboardService } from "@/services/dashboardService"
import type { QuizSummaryForStudent } from "@/types/dashboard"

type MplByAttempt = Record<string, number>
// attemptId → subjectId → mplValue
type SubjectMplByAttempt = Record<string, Record<string, number>>

type AttemptRow = {
  attempt: QuizSummaryForStudent
  transmutedGrade: number | null
}

function calculateTransmutedGrade(scorePercent: number, mpl: number): number {
  if (scorePercent >= mpl) {
    if (mpl >= 100)
      return scorePercent === 100 ? 100 : (scorePercent / 100) * 75
    return 75 + ((scorePercent - mpl) / (100 - mpl)) * 25
  }

  return (scorePercent / mpl) * 75
}

function buildInitialMplByAttempt(
  attempts: QuizSummaryForStudent[],
): MplByAttempt {
  return attempts.reduce<MplByAttempt>((values, attempt) => {
    if (attempt.attempt_id && attempt.minimum_passing_level !== null) {
      if (typeof attempt.minimum_passing_level === "number") {
        values[attempt.attempt_id] = attempt.minimum_passing_level
      }
    }
    return values
  }, {})
}

function buildInitialSubjectMpl(
  attempts: QuizSummaryForStudent[],
): SubjectMplByAttempt {
  return attempts.reduce<SubjectMplByAttempt>((acc, attempt) => {
    if (attempt.attempt_id && attempt.subject_mpl_values) {
      acc[attempt.attempt_id] = { ...attempt.subject_mpl_values }
    }
    return acc
  }, {})
}

export function StudentMplTransmutationCard({
  completedAttempts,
}: {
  completedAttempts: QuizSummaryForStudent[]
}) {
  const eligibleAttempts = completedAttempts.filter(
    (attempt) => attempt.allow_mpl_transmutation,
  )
  const [mplByAttempt, setMplByAttempt] = useState<MplByAttempt>(() =>
    buildInitialMplByAttempt(eligibleAttempts),
  )
  const [subjectMplByAttempt, setSubjectMplByAttempt] =
    useState<SubjectMplByAttempt>(() =>
      buildInitialSubjectMpl(eligibleAttempts),
    )
  const [isHelpOpen, setIsHelpOpen] = useState(false)

  const updateMpl = useMutation({
    mutationFn: DashboardService.updateAttemptMpl,
  })
  const updateSubjectMpl = useMutation({
    mutationFn: DashboardService.updateAttemptSubjectMpl,
  })

  const attemptRows = eligibleAttempts.map<AttemptRow>((attempt) => {
    const attemptId = attempt.attempt_id
    const mpl = attemptId ? mplByAttempt[attemptId] : undefined
    const scorePercent =
      attempt.score !== null && attempt.total_questions > 0
        ? (attempt.score / attempt.total_questions) * 100
        : null

    return {
      attempt,
      transmutedGrade:
        scorePercent !== null && typeof mpl === "number"
          ? calculateTransmutedGrade(scorePercent, mpl)
          : null,
    }
  })

  const tagGroups = Array.from(
    attemptRows
      .reduce<Map<string, AttemptRow[]>>((groups, row) => {
        const tags = row.attempt.tags?.length ? row.attempt.tags : ["untagged"]
        for (const tag of tags) {
          const rows = groups.get(tag) ?? []
          rows.push(row)
          groups.set(tag, rows)
        }
        return groups
      }, new Map())
      .entries(),
  )
    .map(([tag, rows]) => {
      const gradedRows = rows.filter(
        (row) => typeof row.transmutedGrade === "number",
      )
      return {
        tag,
        rows,
        average:
          gradedRows.length > 0
            ? gradedRows.reduce(
                (sum, row) => sum + (row.transmutedGrade ?? 0),
                0,
              ) / gradedRows.length
            : null,
      }
    })
    .sort((a, b) => a.tag.localeCompare(b.tag))

  function handleMplChange(attemptId: string, value: string) {
    const parsed = Number(value)
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 100) {
      setMplByAttempt((current) => {
        const next = { ...current }
        delete next[attemptId]
        return next
      })
      return
    }

    setMplByAttempt((current) => ({ ...current, [attemptId]: parsed }))
  }

  function saveMpl(attemptId: string) {
    const mpl = mplByAttempt[attemptId]
    if (typeof mpl !== "number") return

    updateMpl.mutate({
      attemptId,
      minimumPassingLevel: mpl,
    })
  }

  function handleSubjectMplChange(
    attemptId: string,
    subjectId: string,
    value: string,
  ) {
    const parsed = Number(value)
    setSubjectMplByAttempt((current) => {
      const forAttempt = { ...(current[attemptId] ?? {}) }
      if (!Number.isInteger(parsed) || parsed < 1 || parsed > 100) {
        delete forAttempt[subjectId]
      } else {
        forAttempt[subjectId] = parsed
      }
      return { ...current, [attemptId]: forAttempt }
    })
  }

  function saveSubjectMpl(attemptId: string) {
    const values = subjectMplByAttempt[attemptId] ?? {}
    updateSubjectMpl.mutate({ attemptId, subjectMplValues: values })
  }

  if (eligibleAttempts.length === 0) return null

  function renderGradeValue(grade: number | null) {
    if (grade === null) return "Enter MPL"

    return (
      <Text
        as="span"
        fontWeight="semibold"
        style={{
          color: grade >= 75 ? "var(--chakra-colors-green-600)" : undefined,
        }}
      >
        {grade.toFixed(1)}%
      </Text>
    )
  }

  function renderSubjectGrade(grade: number | null) {
    if (grade === null) return <Text as="span">—</Text>
    return (
      <Text
        as="span"
        fontWeight="semibold"
        style={{
          color: grade >= 75 ? "var(--chakra-colors-green-600)" : undefined,
        }}
      >
        {grade.toFixed(1)}%
      </Text>
    )
  }

  return (
    <Card.Root>
      <Card.Header>
        <HStack align="center" gap={2}>
          <Heading size="md">MPL Transmuted Grades</Heading>
          <IconButton
            aria-label="How MPL transmutation works"
            colorPalette="blue"
            onClick={() => setIsHelpOpen(true)}
            size="xs"
            variant="ghost"
          >
            <FiHelpCircle />
          </IconButton>
        </HStack>
        <Text color="fg.muted" fontSize="sm" mt={1}>
          Set each quiz&apos;s minimum passing level to calculate transmuted
          grades and tag averages.
        </Text>
      </Card.Header>
      <Card.Body>
        <Stack gap={5}>
          {tagGroups.map(({ tag, rows, average }) => (
            <Box
              borderWidth="1px"
              borderRadius="md"
              data-testid={`mpl-tag-group-${tag}`}
              key={tag}
              p={3}
            >
              <Heading size="sm" mb={3}>
                {tag}
              </Heading>

              {/* Desktop */}
              <Box display={{ base: "none", md: "block" }}>
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Quiz</Table.ColumnHeader>
                      <Table.ColumnHeader>Score</Table.ColumnHeader>
                      <Table.ColumnHeader>
                        MPL (State as percentage)
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>Transmuted Grade</Table.ColumnHeader>
                      <Table.ColumnHeader />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {rows.map(({ attempt, transmutedGrade }) => {
                      const attemptId = attempt.attempt_id
                      const mpl = attemptId
                        ? mplByAttempt[attemptId]
                        : undefined
                      const hasSubjectRows =
                        attempt.mpl_per_subject &&
                        (attempt.subject_scores?.length ?? 0) >= 2

                      return (
                        <Fragment key={`${tag}-${attempt.quiz_id}`}>
                          <Table.Row>
                            <Table.Cell>{attempt.quiz_name}</Table.Cell>
                            <Table.Cell>
                              {attempt.score !== null
                                ? `${attempt.score}/${attempt.total_questions}`
                                : "Pending"}
                            </Table.Cell>
                            <Table.Cell>
                              {attemptId ? (
                                <Tooltip
                                  content="Suggested MPL range is 50-60"
                                  openDelay={0}
                                  positioning={{ placement: "top" }}
                                >
                                  <Input
                                    aria-label={`MPL for ${attempt.quiz_name} in ${tag}`}
                                    max={100}
                                    min={1}
                                    onBlur={() => saveMpl(attemptId)}
                                    onChange={(event) =>
                                      handleMplChange(
                                        attemptId,
                                        event.target.value,
                                      )
                                    }
                                    type="number"
                                    value={mpl ?? ""}
                                    width="100px"
                                  />
                                </Tooltip>
                              ) : (
                                "N/A"
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              {renderGradeValue(transmutedGrade)}
                            </Table.Cell>
                            <Table.Cell>
                              {attemptId && (
                                <Button asChild size="xs" variant="outline">
                                  <Link
                                    to="/quizzes/$quizId/attempts/$attemptId/review"
                                    params={{
                                      quizId: attempt.quiz_id,
                                      attemptId,
                                    }}
                                  >
                                    Review
                                  </Link>
                                </Button>
                              )}
                            </Table.Cell>
                          </Table.Row>

                          {hasSubjectRows &&
                            attemptId &&
                            attempt.subject_scores!.map((subj) => {
                              const subjectMpl =
                                subjectMplByAttempt[attemptId]?.[
                                  subj.subject_id
                                ]
                              const scorePercent =
                                subj.total > 0
                                  ? (subj.correct / subj.total) * 100
                                  : null
                              const subjectGrade =
                                scorePercent !== null &&
                                typeof subjectMpl === "number"
                                  ? calculateTransmutedGrade(
                                      scorePercent,
                                      subjectMpl,
                                    )
                                  : null

                              return (
                                <Table.Row
                                  bg="bg.subtle"
                                  data-testid={`subject-row-${subj.subject_name}`}
                                  key={`${attemptId}-subj-${subj.subject_id}`}
                                >
                                  <Table.Cell
                                    color="fg.muted"
                                    fontSize="sm"
                                    pl={8}
                                  >
                                    {subj.subject_name}
                                  </Table.Cell>
                                  <Table.Cell fontSize="sm">
                                    {`${subj.correct}/${subj.total}`}
                                  </Table.Cell>
                                  <Table.Cell>
                                    <Input
                                      aria-label={`Subject MPL for ${subj.subject_name}`}
                                      max={100}
                                      min={1}
                                      onBlur={() => saveSubjectMpl(attemptId)}
                                      onChange={(event) =>
                                        handleSubjectMplChange(
                                          attemptId,
                                          subj.subject_id,
                                          event.target.value,
                                        )
                                      }
                                      size="sm"
                                      type="number"
                                      value={subjectMpl ?? ""}
                                      width="100px"
                                    />
                                  </Table.Cell>
                                  <Table.Cell
                                    data-testid={`subject-transmuted-${subj.subject_name}`}
                                    fontSize="sm"
                                  >
                                    {renderSubjectGrade(subjectGrade)}
                                  </Table.Cell>
                                  <Table.Cell />
                                </Table.Row>
                              )
                            })}
                        </Fragment>
                      )
                    })}
                    <Table.Row>
                      <Table.Cell fontWeight="bold">Total</Table.Cell>
                      <Table.Cell />
                      <Table.Cell />
                      <Table.Cell>{renderGradeValue(average)}</Table.Cell>
                      <Table.Cell />
                    </Table.Row>
                  </Table.Body>
                </Table.Root>
              </Box>

              {/* Mobile cards */}
              <VStack
                display={{ base: "flex", md: "none" }}
                gap={3}
                align="stretch"
              >
                {rows.map(({ attempt, transmutedGrade }) => {
                  const attemptId = attempt.attempt_id
                  const mpl = attemptId ? mplByAttempt[attemptId] : undefined
                  const hasSubjectRows =
                    attempt.mpl_per_subject &&
                    (attempt.subject_scores?.length ?? 0) >= 2

                  return (
                    <Card.Root key={`mobile-${tag}-${attempt.quiz_id}`}>
                      <Card.Body>
                        <Text fontWeight="medium">{attempt.quiz_name}</Text>
                        <Text fontSize="sm" color="fg.muted" mt={1}>
                          Score:{" "}
                          {attempt.score !== null
                            ? `${attempt.score}/${attempt.total_questions}`
                            : "Pending"}
                        </Text>
                        <Box mt={2}>
                          <Text fontSize="xs" color="fg.muted" mb={1}>
                            MPL (State as percentage)
                          </Text>
                          {attemptId ? (
                            <Tooltip
                              content="Suggested MPL range is 50-60"
                              openDelay={0}
                              positioning={{ placement: "top" }}
                            >
                              <Input
                                aria-label={`MPL for ${attempt.quiz_name} in ${tag}`}
                                max={100}
                                min={1}
                                onBlur={() => saveMpl(attemptId)}
                                onChange={(event) =>
                                  handleMplChange(attemptId, event.target.value)
                                }
                                type="number"
                                value={mpl ?? ""}
                                w="100%"
                              />
                            </Tooltip>
                          ) : (
                            <Text fontSize="sm">N/A</Text>
                          )}
                        </Box>
                        <Flex justify="space-between" align="center" mt={2}>
                          <Box>
                            <Text fontSize="xs" color="fg.muted">
                              Transmuted Grade
                            </Text>
                            <Text fontSize="sm">
                              {renderGradeValue(transmutedGrade)}
                            </Text>
                          </Box>
                          {attemptId && (
                            <Button asChild size="xs" variant="outline">
                              <Link
                                to="/quizzes/$quizId/attempts/$attemptId/review"
                                params={{ quizId: attempt.quiz_id, attemptId }}
                              >
                                Review
                              </Link>
                            </Button>
                          )}
                        </Flex>
                        {hasSubjectRows && attemptId && (
                          <Stack gap={2} mt={3} pt={3} borderTopWidth="1px">
                            {attempt.subject_scores!.map((subj) => {
                              const subjectMpl =
                                subjectMplByAttempt[attemptId]?.[
                                  subj.subject_id
                                ]
                              const scorePercent =
                                subj.total > 0
                                  ? (subj.correct / subj.total) * 100
                                  : null
                              const subjectGrade =
                                scorePercent !== null &&
                                typeof subjectMpl === "number"
                                  ? calculateTransmutedGrade(
                                      scorePercent,
                                      subjectMpl,
                                    )
                                  : null

                              return (
                                <Box
                                  key={`mobile-${attemptId}-subj-${subj.subject_id}`}
                                >
                                  <Text
                                    fontSize="xs"
                                    fontWeight="medium"
                                    mb={1}
                                  >
                                    {subj.subject_name}
                                  </Text>
                                  <SimpleGrid columns={2} gap={2}>
                                    <Box>
                                      <Text fontSize="xs" color="fg.muted">
                                        Score
                                      </Text>
                                      <Text fontSize="sm">{`${subj.correct}/${subj.total}`}</Text>
                                    </Box>
                                    <Box>
                                      <Text fontSize="xs" color="fg.muted">
                                        Subject MPL
                                      </Text>
                                      <Input
                                        aria-label={`Subject MPL for ${subj.subject_name}`}
                                        max={100}
                                        min={1}
                                        onBlur={() => saveSubjectMpl(attemptId)}
                                        onChange={(event) =>
                                          handleSubjectMplChange(
                                            attemptId,
                                            subj.subject_id,
                                            event.target.value,
                                          )
                                        }
                                        size="sm"
                                        type="number"
                                        value={subjectMpl ?? ""}
                                        w="100%"
                                      />
                                    </Box>
                                    <Box>
                                      <Text fontSize="xs" color="fg.muted">
                                        Transmuted Grade
                                      </Text>
                                      <Text fontSize="sm">
                                        {renderSubjectGrade(subjectGrade)}
                                      </Text>
                                    </Box>
                                  </SimpleGrid>
                                </Box>
                              )
                            })}
                          </Stack>
                        )}
                      </Card.Body>
                    </Card.Root>
                  )
                })}
                {/* Total row */}
                <Card.Root>
                  <Card.Body>
                    <Flex justify="space-between">
                      <Text fontWeight="bold">Total</Text>
                      <Text fontWeight="bold">{renderGradeValue(average)}</Text>
                    </Flex>
                  </Card.Body>
                </Card.Root>
              </VStack>
            </Box>
          ))}
        </Stack>
      </Card.Body>
      <DialogRoot
        open={isHelpOpen}
        onOpenChange={({ open }) => setIsHelpOpen(open)}
        size={{ base: "sm", md: "lg" }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>How MPL transmutation works</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />

          <DialogBody>
            <VStack align="stretch" gap={5}>
              <Text color="fg.muted" fontSize="sm">
                Raw score percent is compared with your MPL. The MPL is the
                score percent that should count as the passing grade.
              </Text>

              <Flex
                align="stretch"
                borderRadius="lg"
                overflow="hidden"
                borderWidth="1px"
              >
                <Box bg="red.50" flex="1" p={3}>
                  <Text fontSize="xs" fontWeight="bold" color="red.700">
                    Below MPL
                  </Text>
                  <Text fontSize="sm">Scales up to 75%</Text>
                </Box>
                <Box bg="green.50" borderLeftWidth="1px" p={3}>
                  <Text fontSize="xs" fontWeight="bold" color="green.700">
                    MPL
                  </Text>
                  <Text fontSize="sm">75%</Text>
                </Box>
                <Box bg="blue.50" borderLeftWidth="1px" flex="1" p={3}>
                  <Text fontSize="xs" fontWeight="bold" color="blue.700">
                    Above MPL
                  </Text>
                  <Text fontSize="sm">Scales from 75% to 100%</Text>
                </Box>
              </Flex>

              <Stack gap={3}>
                <Box borderWidth="1px" borderRadius="md" p={3}>
                  <Text fontWeight="semibold">Passing anchor</Text>
                  <Text color="fg.muted" fontSize="sm">
                    A score at the MPL becomes 75%.
                  </Text>
                </Box>
                <Box borderWidth="1px" borderRadius="md" p={3}>
                  <Text fontWeight="semibold">Perfect score anchor</Text>
                  <Text color="fg.muted" fontSize="sm">
                    100% stays 100%.
                  </Text>
                </Box>
                <Box borderWidth="1px" borderRadius="md" p={3}>
                  <Text fontWeight="semibold">Tag totals</Text>
                  <Text color="fg.muted" fontSize="sm">
                    Each tag total averages the transmuted grades in that tag.
                  </Text>
                </Box>
              </Stack>

              <Box bg="bg.subtle" borderRadius="md" p={3}>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                  Example
                </Text>
                <Text color="fg.muted" fontSize="sm">
                  If MPL is 60 and your raw score is 60%, the transmuted grade
                  is 75%. If your raw score is higher than 60%, the extra points
                  are spread between 75% and 100%.
                </Text>
              </Box>
            </VStack>
          </DialogBody>

          <DialogFooter>
            <Button onClick={() => setIsHelpOpen(false)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Card.Root>
  )
}
